import React, { useState, useEffect } from 'react';
import { ProjectPhaseDetail, EditableProjectPhaseDetail } from '../types';
import { apiService } from '../services/api';

interface ProjectPhasesEditorProps {
  isOpen: boolean;
  onClose: () => void;
  clientId: string;
  initialOverallStatus: string;
  initialPhases: ProjectPhaseDetail[];
  onSave: (updatedPhases: ProjectPhaseDetail[], updatedOverallStatus?: string) => void;
}

const ProjectPhasesEditor: React.FC<ProjectPhasesEditorProps> = ({
  isOpen,
  onClose,
  clientId,
  initialOverallStatus,
  initialPhases,
  onSave
}) => {
  const [phases, setPhases] = useState<EditableProjectPhaseDetail[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [overallStatus, setOverallStatus] = useState<string>('');

  useEffect(() => {
    if (isOpen) {
      // Convert ProjectPhaseDetail[] to EditableProjectPhaseDetail[]
      const editablePhases: EditableProjectPhaseDetail[] = initialPhases.map((phase, index) => ({
        ...phase,
        order_index: index
      }));
      setOverallStatus(initialOverallStatus);
      setPhases(editablePhases);
      setError(null);
    }
  }, [isOpen, initialPhases, initialOverallStatus]);

  const handlePhaseChange = (id: string, field: keyof ProjectPhaseDetail, value: string | number) => {
    setPhases(prev => prev.map(phase => 
      phase.id === id ? { ...phase, [field]: value } : phase
    ));
  };

  const addPhase = () => {
    const newPhase: EditableProjectPhaseDetail = {
      id: `temp_${Date.now()}`,
      phaseNumber: phases.filter(p => !p.isDeleted).length + 1,
      name: '',
      description: '',
      targetDate: '',
      progress: 0,
      milestone: '',
      status: 'pending',
      order_index: phases.filter(p => !p.isDeleted).length,
      isNew: true
    };
    setPhases(prev => [...prev, newPhase]);
  };

  const removePhase = (id: string) => {
    setPhases(prev => prev.map(phase => 
      phase.id === id ? { ...phase, isDeleted: true } : phase
    ));
  };

  const movePhaseUp = (id: string) => {
    const activePhases = phases.filter(phase => !phase.isDeleted);
    const currentIndex = activePhases.findIndex(phase => phase.id === id);
    
    if (currentIndex > 0) {
      const newPhases = [...phases];
      const phaseToMove = newPhases.find(phase => phase.id === id);
      const phaseToSwap = newPhases.find(phase => phase.id === activePhases[currentIndex - 1].id);
      
      if (phaseToMove && phaseToSwap) {
        const tempOrder = phaseToMove.order_index;
        const tempPhaseNumber = phaseToMove.phaseNumber;
        
        phaseToMove.order_index = phaseToSwap.order_index;
        phaseToMove.phaseNumber = phaseToSwap.phaseNumber;
        phaseToSwap.order_index = tempOrder;
        phaseToSwap.phaseNumber = tempPhaseNumber;
      }
      
      setPhases(newPhases);
    }
  };

  const movePhaseDown = (id: string) => {
    const activePhases = phases.filter(phase => !phase.isDeleted);
    const currentIndex = activePhases.findIndex(phase => phase.id === id);
    
    if (currentIndex < activePhases.length - 1) {
      const newPhases = [...phases];
      const phaseToMove = newPhases.find(phase => phase.id === id);
      const phaseToSwap = newPhases.find(phase => phase.id === activePhases[currentIndex + 1].id);
      
      if (phaseToMove && phaseToSwap) {
        const tempOrder = phaseToMove.order_index;
        const tempPhaseNumber = phaseToMove.phaseNumber;
        
        phaseToMove.order_index = phaseToSwap.order_index;
        phaseToMove.phaseNumber = phaseToSwap.phaseNumber;
        phaseToSwap.order_index = tempOrder;
        phaseToSwap.phaseNumber = tempPhaseNumber;
      }
      
      setPhases(newPhases);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    setError(null);

    try {
      // Prepare phases for API
      const phasesToSave = phases
        .filter(phase => !phase.isDeleted && phase.name.trim())
        .sort((a, b) => (a.order_index || 0) - (b.order_index || 0))
        .map((phase, index) => ({
          phaseNumber: index + 1,
          name: phase.name,
          description: phase.description,
          targetDate: phase.targetDate,
          progress: Number(phase.progress) || 0,
          milestone: phase.milestone || null,
          status: phase.status,
          order_index: index
        }));

      const updatedProjectStatus: any = await apiService.updateProjectPhases(clientId, phasesToSave, overallStatus);
      onSave(updatedProjectStatus?.allPhases || [], updatedProjectStatus?.overallStatus);
      onClose();
    } catch (err) {
      console.error('Failed to save project phases:', err);
      setError('Failed to save changes. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const activePhases = phases
    .filter(phase => !phase.isDeleted)
    .sort((a, b) => (a.order_index || 0) - (b.order_index || 0));

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          maxWidth: '1000px',
          width: '90%',
          maxHeight: '80vh',
          overflowY: 'auto',
          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ padding: '24px 24px 0 24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: '600', margin: 0, color: '#1a1a1a' }}>
              Edit Project Phases
            </h2>
            <div>
              <button
                onClick={onClose}
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: 'none',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  fontFamily: 'Inter, sans-serif',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  marginRight: '8px',
                }}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: 'none',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  fontFamily: 'Inter, sans-serif',
                  backgroundColor: '#007bff',
                  color: 'white',
                }}
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>

          {error && (
            <div style={{ 
              backgroundColor: '#f8d7da', 
              color: '#721c24', 
              padding: '12px', 
              borderRadius: '6px', 
              marginBottom: '20px',
              border: '1px solid #f5c6cb'
            }}>
              {error}
            </div>
          )}
        </div>

        {/* Content */}
        <div style={{ padding: '24px' }}>
          {/* Add Button */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#374151', fontSize: '14px' }}>
              Overall Status
            </label>
            <input
              type="text"
              value={overallStatus}
              onChange={(e) => setOverallStatus(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '1px solid #e1e5e9',
                borderRadius: '8px',
                fontSize: '14px',
                fontFamily: 'Inter, sans-serif',
              }}
              placeholder="Enter overall project status"
            />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <p style={{ margin: 0, color: '#6b7280', fontSize: '14px' }}>
              Manage the project phases and their details.
            </p>
            <button
              onClick={addPhase}
              style={{
                padding: '6px 12px',
                borderRadius: '6px',
                border: 'none',
                fontSize: '12px',
                fontWeight: '500',
                cursor: 'pointer',
                fontFamily: 'Inter, sans-serif',
                backgroundColor: '#28a745',
                color: 'white',
              }}
            >
              + Add Phase
            </button>
          </div>

          {/* Phases List */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {activePhases.map((phase, index) => (
              <div
                key={phase.id}
                style={{
                  border: '1px solid #e1e5e9',
                  borderRadius: '8px',
                  padding: '16px',
                  backgroundColor: phase.isNew ? '#f8f9fa' : 'white',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                  <label style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                    Phase {index + 1}
                  </label>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    {/* Move Up Button */}
                    <button
                      onClick={() => movePhaseUp(phase.id)}
                      disabled={index === 0}
                      style={{
                        backgroundColor: index === 0 ? '#e9ecef' : '#6c757d',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '4px 8px',
                        fontSize: '12px',
                        cursor: index === 0 ? 'not-allowed' : 'pointer',
                      }}
                      title="Move up"
                    >
                      ↑
                    </button>
                    {/* Move Down Button */}
                    <button
                      onClick={() => movePhaseDown(phase.id)}
                      disabled={index === activePhases.length - 1}
                      style={{
                        backgroundColor: index === activePhases.length - 1 ? '#e9ecef' : '#6c757d',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '4px 8px',
                        fontSize: '12px',
                        cursor: index === activePhases.length - 1 ? 'not-allowed' : 'pointer',
                      }}
                      title="Move down"
                    >
                      ↓
                    </button>
                    {/* Remove Button */}
                    <button
                      onClick={() => removePhase(phase.id)}
                      style={{
                        backgroundColor: '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '4px 8px',
                        fontSize: '12px',
                        cursor: 'pointer',
                      }}
                      title="Remove phase"
                    >
                      ×
                    </button>
                  </div>
                </div>
                
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#6c757d' }}>
                      Phase Name
                    </label>
                    <input
                      type="text"
                      value={phase.name}
                      onChange={(e) => handlePhaseChange(phase.id, 'name', e.target.value)}
                      placeholder="e.g., Foundation & Architecture"
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #e1e5e9',
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontFamily: 'Inter, sans-serif',
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#6c757d' }}>
                      Target Date
                    </label>
                    <input
                      type="text"
                      value={phase.targetDate}
                      onChange={(e) => handlePhaseChange(phase.id, 'targetDate', e.target.value)}
                      placeholder="e.g., June 2025"
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #e1e5e9',
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontFamily: 'Inter, sans-serif',
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#6c757d' }}>
                      Status
                    </label>
                    <select
                      value={phase.status}
                      onChange={(e) => handlePhaseChange(phase.id, 'status', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #e1e5e9',
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontFamily: 'Inter, sans-serif',
                      }}
                    >
                      <option value="pending">Pending</option>
                      <option value="in_progress">In Progress</option>
                      <option value="completed">Completed</option>
                    </select>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '12px', marginBottom: '12px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#6c757d' }}>
                      Description
                    </label>
                    <textarea
                      value={phase.description}
                      onChange={(e) => handlePhaseChange(phase.id, 'description', e.target.value)}
                      placeholder="Enter the description of this phase..."
                      style={{
                        width: '100%',
                        minHeight: '60px',
                        padding: '8px 12px',
                        border: '1px solid #e1e5e9',
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontFamily: 'Inter, sans-serif',
                        resize: 'vertical',
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#6c757d' }}>
                      Progress (%)
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={phase.progress}
                      onChange={(e) => handlePhaseChange(phase.id, 'progress', parseInt(e.target.value) || 0)}
                      style={{
                        width: '80px',
                        padding: '8px 12px',
                        border: '1px solid #e1e5e9',
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontFamily: 'Inter, sans-serif',
                      }}
                    />
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#6c757d' }}>
                    Milestone (optional)
                  </label>
                  <input
                    type="text"
                    value={phase.milestone || ''}
                    onChange={(e) => handlePhaseChange(phase.id, 'milestone', e.target.value)}
                    placeholder="e.g., Core search infrastructure deployed"
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #e1e5e9',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontFamily: 'Inter, sans-serif',
                    }}
                  />
                </div>
              </div>
            ))}

            {activePhases.length === 0 && (
              <div style={{ 
                textAlign: 'center', 
                padding: '40px 20px', 
                color: '#6b7280',
                fontSize: '14px'
              }}>
                No project phases yet. Click "Add Phase" to get started.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectPhasesEditor; 